"use client";

import { useEffect, useState } from "react";
import { I18nextProvider } from "react-i18next";
import {
  APP_LOCALE_COOKIE_MAX_AGE_SECONDS,
  APP_LOCALE_COOKIE_NAME,
  type SupportedLocale,
} from "@/lib/i18n/config";
import { createRequestI18n, initI18n } from "@/lib/infra/i18n";

type I18nProviderProps = {
  children: React.ReactNode;
  initialLocale: SupportedLocale;
};

export default function I18nProvider({
  children,
  initialLocale,
}: I18nProviderProps) {
  const [i18n] = useState(() =>
    typeof window === "undefined"
      ? createRequestI18n(initialLocale)
      : initI18n(initialLocale)
  );

  useEffect(() => {
    const persistLocale = (lng: string) => {
      localStorage.setItem("lng", lng);
      document.cookie = `${APP_LOCALE_COOKIE_NAME}=${lng}; Path=/; Max-Age=${APP_LOCALE_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
    };

    const setLangAttr = (lng: string) => {
      if (typeof document !== "undefined") {
        document.documentElement.lang = lng;
      }
    };
    setLangAttr(i18n.language);
    persistLocale(i18n.language);

    const handler = (lng: string) => {
      persistLocale(lng);
      setLangAttr(lng);
    };

    if (i18n.language !== initialLocale) {
      void i18n.changeLanguage(initialLocale);
    }

    i18n.on("languageChanged", handler);
    return () => {
      i18n.off("languageChanged", handler);
    };
  }, [i18n, initialLocale]);

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}
